import type { PostHogContextRef } from '../context'

/**
 * Library tab identifiers, shared with `community_library_tab_changed` and
 * `community_my_library_viewed`.
 */
export type CommunityLibraryTab = 'discover' | 'following' | 'my_playlists'

/**
 * Playlist type (custom user playlist vs. system-curated).
 */
export type CommunityPlaylistType = 'custom' | 'system'

/**
 * Community page-view events. Fire on page mount once data has loaded.
 * Also serve as PostHog session-recording triggers (configured in dashboard).
 *
 * `community_profile_viewed` carries the *viewed* profile's `profile_display_name`
 * (not its UUID). Display names are user-chosen public identifiers.
 * `community_shared_playlist_viewed` carries the playlist *title* (not its id) —
 * shared/viral surfaces use title-only payloads for dashboard legibility.
 */
export type PageViewEvents = {
  community_profile_viewed: (
    isOwnProfile: boolean,
    isLoggedIn: boolean,
    profileDisplayName?: string,
  ) => void
  community_my_library_viewed: (activeTab: CommunityLibraryTab) => void
  community_playlist_viewed: (
    playlistId: string,
    playlistType: CommunityPlaylistType,
    isOwner: boolean
  ) => void
  community_shared_playlist_viewed: (playlistTitle: string, isLoggedIn: boolean) => void
}

export function createPageViewEventCallbacks (
  contextRef: PostHogContextRef,
): PageViewEvents {
  return {
    community_profile_viewed: (
      isOwnProfile: boolean,
      isLoggedIn: boolean,
      profileDisplayName?: string,
    ) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_profile_viewed', {
        context: 'community',
        is_own_profile: isOwnProfile,
        is_logged_in: isLoggedIn,
        ...(profileDisplayName && { profile_display_name: profileDisplayName }),
      })
    },

    community_my_library_viewed: (activeTab: CommunityLibraryTab) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_my_library_viewed', {
        context: 'community',
        active_tab: activeTab,
      })
    },

    community_playlist_viewed: (
      playlistId: string,
      playlistType: CommunityPlaylistType,
      isOwner: boolean,
    ) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_playlist_viewed', {
        context: 'community',
        playlist_id: playlistId,
        playlist_type: playlistType,
        is_owner: isOwner,
      })
    },

    community_shared_playlist_viewed: (playlistTitle: string, isLoggedIn: boolean) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_shared_playlist_viewed', {
        context: 'community',
        playlist_title: playlistTitle,
        is_logged_in: isLoggedIn,
      })
    },
  }
}
